import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import Confirm from '../../components/confirm';
import CustomDataGrid from '../../components/data-grid';
import CustomDataRowSelectionGrid from '../../components/data-grid-selection';
import JobRunTicker from '../../components/job-run ticker';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import API from '../../utils/api';
import {
  apiReponseError, apiRequestError, canReadContractOrder, canReadPreOrder, canWriteContractOrder,
  canWritePreOrder, checkUserAuthenticated, formatDateForDataGrid, formatDateForInput,
  formatDateTimeForDataGrid, getShowDashboardValue, goToRoutePage, redirectRoutePage,
  setWindowTitle, toggleShowDashboard
} from '../../utils/helper';
import {
  ContractOrderIcon, DashboardOffIcon, DashboardOnIcon, MoreIcon, RefreshIcon
} from '../../utils/icons';
import {
  Alert, Box, Breadcrumbs, Button, Card, CardContent, CardHeader, FormControl, FormHelperText,
  Grid, Hidden, IconButton, InputLabel, Link, ListItemIcon, ListItemText, Menu, MenuItem, Select,
  TextField, Tooltip, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow,
} from '../../utils/material-ui-core';

interface AssignmentFilterAttribute {
  kitName: string,
  postingStatus: string,
  chargeableType: string,
  orderType: string,
}

const defaultChartOptionsForBatchMatch = {
  colors: ['#058DC7', '#50B432', '#ED561B', '#DDDF00', '#24CBE5', '#64E572', '#FF9655', '#FFF263', '#6AF9C4'],
  chart: {
    type: 'pie',
    options3d: {
      enabled: false,
      alpha: 45,
      beta: 0
    },
    style: { fontFamily: "Roboto" },
  },
  title: {
    text: '',
    align: 'left',
    margin: 1
  },

  accessibility: {
    point: {
      valueSuffix: '%'
    }
  },
  tooltip: {
    pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
  },
  plotOptions: {
    pie: {
      allowPointSelect: true,
      cursor: 'pointer',
      depth: 35,
      dataLabels: {
        enabled: true,
        format: '{point.name}'
      }
    }
  },

  series: [{
    type: 'pie',
    name: '',
    colorByPoint: true,
    data: [['', 0]]
  }]
}

const defaultAssignmentFilterAttribute = { kitName: "", postingStatus: "", chargeableType: "", orderType: "" };

const defaultConfirmationDialogSetting = {
  title: "",
  content: "",
  yesText: "",
  noText: "CANCEL",
  open: false,
  callbackOnClose: () => { },
  callbackOnConfirm: (confirm: boolean) => { },
};

const PreOrderList = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { isUserAuthenticated, showAlert, userFunctionAccess, referenceLookups } = useContext(AppContext);

  const [anchorElForOptionsMenu, setAnchorElForOptionsMenu] = useState<null | HTMLElement>(null);
  const [actionId, setActionId] = useState<number>(0);
  const [assignmentFilterAttribute, setAssignmentFilterAttribute] = useState<AssignmentFilterAttribute>(defaultAssignmentFilterAttribute);
  const [preorderAssignments, setPreorderAssignments] = useState<any[]>([]);
  const [chartOptionsForBatchMatchSummary, setChartOptionsForBatchMatchSummary] = useState(defaultChartOptionsForBatchMatch);
  const [chartOptionsForPreOrderTypeSummary, setChartOptionsForPreOrderTypeSummary] = useState(defaultChartOptionsForBatchMatch);
  const [confirmationDialogSetting, setConfirmationDialogSetting] = useState<ConfirmationDialogProbs>(defaultConfirmationDialogSetting);
  const [contractOrderKey, setContractOrderKey] = useState<string>("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [contractOrders, setContractOrders] = useState<ContractOrder[]>([]);
  const [contractOrderDistinctAttributes, setContractOrderDistinctAttributes] = useState<ContractOrderDistinctAttribute[]>([]);
  const [filterAssignments, setFilterAssignments] = useState<any[]>([]);
  const [isContractOrderExists, setIsContractOrderExists] = useState<boolean>(true);
  const [isPostingStatusRequired, setIsPostingStatusRequired] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [pendings, setPendings] = useState<Pending[]>([]);
  const [pendingPrograms, setPendingPrograms] = useState<pendingProgram[]>([]);
  const [pendingSummaries, setPendingSummaries] = useState<PendingSummaries | null>(null);
  const [programKey, setProgramKey] = useState("");
  const [readonly, setReadonly] = useState<boolean>(false);
  const [reason, setReason] = useState<string>("");
  const [selectedPostingStatus, setSelectedPostingStatus] = useState("");
  const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
  const [showDashboard, setShowDashboard] = useState<boolean>(true);
  const [statusId, setStatusId] = useState(0);
  const [dataChange, setDataChange] = useState(false);

  const [crmInteractionId, setCRMInteractionId] = useState<string>("");
  const [preOrderCRMCases, setPreOrderCRMCases] = React.useState<PreOrderCRMCase[]>([]);

  const columnsForPendingPrograms = [
    { field: "clientCompanyName", headerName: "Client Company", width: 400 },
    { field: "programName", headerName: "Program Name", width: 400 },
    { field: "programCode", headerName: "Program Code", width: 200, hide: true },
    { field: "reviewCount", headerName: "Review Count", width: 200, type: "number" },
    { field: "contractOrderExists", headerName: "Contract Exists", width: 200, type: "boolean" },
    { field: "minCreatedDate", headerName: "Pending Date (Min)", width: 210, type: "date", valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "maxCreatedDate", headerName: "Pending Date (Max)", width: 210, type: "date", valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
  ];

  const columns = [
    { field: "preOrderType", headerName: "Source", width: 200, hide: false },
    { field: "clientCompanyName", headerName: "Client Company", width: 300, hide: true },
    { field: "programName", headerName: "Program", width: 300, hide: true },
    { field: "status", headerName: "Status", width: 300, hide: true },
    { field: "folderId", headerName: "Folder Id", width: 200, type: "number", hide: true },
    { field: "folderName", headerName: "Folder Name", width: 300, hide: false },
    { field: "contractOrder", headerName: "Contract Order Number", width: 300, hide: true },
    { field: "contractOrderDate", headerName: "Contract Order Date", width: 200, type: "date", hide: true, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "deviceOwnership", headerName: "Device Ownership", width: 300, hide: false },
    { field: "chargeableType", headerName: "Chargable Type", width: 300, hide: false },
    { field: "postingStatus", headerName: "Posting Status", width: 300, hide: false },
    { field: "orderType", headerName: "Order Type", width: 300, hide: true },
    { field: "installOption", headerName: "Install Option", width: 300, hide: true },
    { field: "kitName", headerName: "Kit name", width: 300, hide: true },
    { field: "kitnumber", headerName: "Kit No", width: 200, type: "number", hide: true },
    { field: "fulfillmentVendor", headerName: "Fulfillment Vendor", width: 300, hide: true },
    { field: "platformVendor", headerName: "Platform Vendor", width: 300, hide: true },
    { field: "taxExempt", headerName: "Tax Exempt", width: 200, type: "boolean", hide: true },
    { field: "billableFolderId", headerName: "Billable Folder Id", width: 200, type: "number", hide: true },
    { field: "cancellationReason", headerName: "Cancellation Reason", width: 300, hide: true },
    { field: "errorReason", headerName: "Error Reason", width: 300, hide: true },
    { field: "crmCaseNumber", headerName: "CRM Case#", width: 300, hide: true },
    { field: "crmInstallDate", headerName: "CRM Install Date", width: 200, type: "date", hide: true, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "crmInteractionId", headerName: "CRM Case Interaction Id", width: 300, hide: true },
    { field: "crmInstallComments", headerName: "CRM Case Install Comments", width: 300, hide: true },
    { field: "crmInstallOverride", headerName: "CRM Case Install Override", width: 300, hide: true, type: "boolean" },
    { field: "crmInstallOverrideBy", headerName: "CRM Case Install Override By", width: 300, hide: true },
    { field: "crmInstallOverrideDateTime", headerName: "CRM Case Install Override Date", width: 200, hide: true, type: "dateTime", valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "orderId", headerName: "Order Id", width: 300, hide: true },
    { field: "shortOrderId", headerName: "Short Order Id", width: 300, hide: true },
    { field: "subscriptionNodeId", headerName: "Subscription Node Id", width: 200, type: "number", hide: true },
    { field: "orderNodeId", headerName: "Order Node Id", width: 200, type: "number", hide: true },
    { field: "orderJson", headerName: "Order Json", width: 300, hide: true },
    { field: "orderSubmittedDate", headerName: "Order Submitted Date", width: 200, type: "dateTime", hide: true, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "salesOrderNumber", headerName: "salesOrderNumber", width: 300, hide: true },
    { field: "sentToD365Date", headerName: "Sent To D365 Date", width: 200, type: "date", hide: true, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "lastModifiedBy", headerName: "Last Modified By", width: 300, hide: true },
    { field: "lastModifiedDateTime", headerName: "Last Modified Date", width: 200, type: "dateTime", valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "preOrderKey", headerName: "Key", width: 300, hide: true },
  ];

  const crmColumns = [
    { field: "preOrderType", headerName: "Source", width: 200, hide: false },
    { field: "clientCompanyName", headerName: "Client Company", width: 300, hide: true },
    { field: "programName", headerName: "Program", width: 300, hide: true },
    { field: "status", headerName: "Status", width: 300, hide: true },
    { field: "folderId", headerName: "Folder Id", width: 200, type: "number", hide: true },
    { field: "folderName", headerName: "Folder Name", width: 300, hide: false },
    { field: "orderDate", headerName: "Order Process Date", width: 220, type: "date", hide: false, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "crmInstallDate", headerName: "Install Date", width: 200, type: "date", hide: false, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "crmCaseNumber", headerName: "CRM Case#", width: 300, hide: false },
    { field: "crmInteractionId", headerName: "CRM Case Interaction Id", width: 300, hide: false },
    { field: "crmInstallComments", headerName: "CRM Case Install Comments", width: 300, hide: false },
    { field: "crmInstallOverride", headerName: "CRM Case Install Override", width: 300, hide: false, type: "boolean" },
    { field: "crmInstallOverrideBy", headerName: "CRM Case Install Override By", width: 300, hide: false },
    { field: "crmInstallOverrideDateTime", headerName: "CRM Case Install Override Date", width: 200, type: "dateTime", valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "deviceOwnership", headerName: "Device Ownership", width: 300, hide: true },
    { field: "chargeableType", headerName: "Chargable Type", width: 300, hide: true },
    { field: "postingStatus", headerName: "Posting Status", width: 300, hide: true },
    { field: "orderType", headerName: "Order Type", width: 300, hide: true },
    { field: "installOption", headerName: "Install Option", width: 300, hide: true },
    { field: "kitName", headerName: "Kit name", width: 300, hide: true },
    { field: "kitnumber", headerName: "Kit No", width: 200, type: "number", hide: true },
    { field: "fulfillmentVendor", headerName: "Fulfillment Vendor", width: 300, hide: true },
    { field: "platformVendor", headerName: "Platform Vendor", width: 300, hide: true },
    { field: "taxExempt", headerName: "Tax Exempt", width: 200, type: "boolean", hide: true },
    { field: "billableFolderId", headerName: "Billable Folder Id", width: 200, type: "number", hide: true },
    { field: "cancellationReason", headerName: "Cancellation Reason", width: 300, hide: true },
    { field: "errorReason", headerName: "Error Reason", width: 300, hide: true },
    { field: "lastModifiedBy", headerName: "Last Modified By", width: 300, hide: true },
    { field: "lastModifiedDateTime", headerName: "Last Modified Date", width: 200, type: "dateTime", valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "preOrderKey", headerName: "Key", width: 300, hide: true },
  ];

  // All confirmations from user
  let confirmationDialogSettingName = "";

  // Options menu related func
  const handleAnchorElOptionsMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElForOptionsMenu(event.currentTarget);
  };

  const handleAnchorElOptionsMenuClose = () => {
    setAnchorElForOptionsMenu(null);
  };

  const StyledMenuItem = withStyles((theme) => ({
    root: {
      "&:focus": {
        backgroundColor: theme.palette.secondary.main,
        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
          color: theme.palette.common.white,
        },
      },
    },
  }))(MenuItem);

  // Open confirmation for user action
  const openConfirmationDialog = (statusname) => {
    const tempConfirmationDialogSetting: ConfirmationDialogProbs = JSON.parse(JSON.stringify(defaultConfirmationDialogSetting));

    confirmationDialogSettingName = statusname;
    tempConfirmationDialogSetting.title = "CONFIRMATION";
    tempConfirmationDialogSetting.yesText = "CONFIRM";
    tempConfirmationDialogSetting.noText = "CANCEL";
    tempConfirmationDialogSetting.open = true;
    tempConfirmationDialogSetting.callbackOnClose = closeConfirmationDialog;
    tempConfirmationDialogSetting.callbackOnConfirm = resultConfirmationDialog;

    if (statusname === "nco") {
      tempConfirmationDialogSetting.content = "Would you like to initiate a new contract order?";
      setConfirmationDialogSetting(tempConfirmationDialogSetting);
    }
    else if (statusname === "updateassignments") {

      if (actionId === 1) {
        var filterContractOrders = contractOrderDistinctAttributes.filter(contractOrder => contractOrder.contractOrderKey === contractOrderKey);

        if (filterContractOrders.length === 1) {
          tempConfirmationDialogSetting.content = "Would you like to update the chosen contract order (<u style='color:brown'>" + filterContractOrders[0].contractOrderNumber + "</u>) for all <u style='color:brown'>" + selectedRows.length + "</u> preorders?";
          setConfirmationDialogSetting(tempConfirmationDialogSetting);
        }
      }
      else if (actionId === 2) {
        tempConfirmationDialogSetting.title = String("Confirmation").toUpperCase();
        tempConfirmationDialogSetting.content = "Do you want to cancel all selected  <u style='color:brown'>" + selectedRows.length + "</u> preorders?";
        setConfirmationDialogSetting(tempConfirmationDialogSetting);
      }
      else if (actionId === 3) {
        tempConfirmationDialogSetting.title = String("Confirmation").toUpperCase();
        tempConfirmationDialogSetting.content = "Do you want to cancel & opt out of all selected <u style='color:brown'>" + selectedRows.length + "</u> preorders?";
        setConfirmationDialogSetting(tempConfirmationDialogSetting);
      }
      else if (actionId === 4) {
        tempConfirmationDialogSetting.title = String("Confirmation").toUpperCase();
        tempConfirmationDialogSetting.content = "Do you want to put a hold on all selected <u style='color:brown'>" + selectedRows.length + "</u> preorders?";
        setConfirmationDialogSetting(tempConfirmationDialogSetting);
      }
      else if (actionId === 5) {
        tempConfirmationDialogSetting.title = String("Confirmation").toUpperCase();
        tempConfirmationDialogSetting.content = "Do you want to reprocess all selected <u style='color:brown'>" + selectedRows.length + "</u> preorders?";
        setConfirmationDialogSetting(tempConfirmationDialogSetting);
      }
      else if (actionId === 6) {
        tempConfirmationDialogSetting.title = String("Confirmation").toUpperCase();
        tempConfirmationDialogSetting.content = "Do you want to reactive all selected <u style='color:brown'>" + selectedRows.length + "</u> preorders?";
        setConfirmationDialogSetting(tempConfirmationDialogSetting);
      }
      else if (actionId === 7) {
        var filterCases = preOrderCRMCases.filter(x => x.interactionId === crmInteractionId);

        if (filterCases.length === 1) {
          tempConfirmationDialogSetting.content = "Would you like to assign the CRM case number (<u style='color:brown'>" + filterCases[0].caseNumber + "</u>) to the selected preorder?";
          setConfirmationDialogSetting(tempConfirmationDialogSetting);
        }
      }
      else if (actionId === 8) {
        tempConfirmationDialogSetting.title = String("Confirmation").toUpperCase();
        tempConfirmationDialogSetting.content = "Do you want to override the installation date for all selected <u style='color:brown'>" + selectedRows.length + "</u> preorders and process them immediately?";
        setConfirmationDialogSetting(tempConfirmationDialogSetting);
      }
    }
  };

  // Confirmation result for user action
  const resultConfirmationDialog = async (confirm: boolean) => {
    if (confirm) {
      if (confirmationDialogSettingName === "nco") {
        newContractOrder();
      }
      else if (confirmationDialogSettingName === "updateassignments") {
        savePendingAssignments();
      }
    }

    setConfirmationDialogSetting(defaultConfirmationDialogSetting);
    confirmationDialogSettingName = "";
  };

  // Close confirmation for user action
  const closeConfirmationDialog = () => {
    setConfirmationDialogSetting(defaultConfirmationDialogSetting);
    confirmationDialogSettingName = "";
  };

  // When use click pending assigment grid row
  const handleSelectedRowsForPendingAssignments = (rows: any[]) => {
    const allPostingStatus: any = [];

    setIsPostingStatusRequired(false);
    setIsContractOrderExists(true);
    setSelectedPostingStatus("");

    rows.forEach(row => {
      if (allPostingStatus.indexOf(row.postingStatus) === -1) {
        allPostingStatus.push(row.postingStatus);
      }
    });

    if (allPostingStatus.length > 1) {
      setIsPostingStatusRequired(true);
    }

    if (allPostingStatus.length === 1) {
      setSelectedPostingStatus(allPostingStatus[0]);
      if (contractOrderDistinctAttributes.length > 0) {
        if (contractOrderDistinctAttributes.filter(x => x.postingStatus === allPostingStatus[0]).length > 0) {
          setIsContractOrderExists(true);
        }
        else {
          setIsContractOrderExists(false);
        }
      }
      else {
        setIsContractOrderExists(false);
      }
    }

    setContractOrderKey("");
    setSelectedRows(rows);

  }

  // Reset all key variables
  const resetAll = () => {

    if (dataChange) {
      redirectRoutePage('preordercorrections');
      return;
    }

    setActionId(0);
    setProgramKey("");
    setPreorderAssignments([]);
    setFilterAssignments([]);
    setAssignmentFilterAttribute(defaultAssignmentFilterAttribute);
    setSelectedRows([]);
    setContractOrders([]);
    setContractOrderDistinctAttributes([]);
    setIsPostingStatusRequired(false);
    setIsContractOrderExists(true);
    setDataChange(false);
  }

  // Add new contract order
  const newContractOrder = () => {

    if (!canWriteContractOrder(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      goToRoutePage("home");
      return;
    }

    goToRoutePage("contractorders/" + referenceLookups.newRecordDefaultId);
  }

  // Show/hide update button
  const showUpdateButton = () => {

    if (selectedRows.length > 0) {
      if (actionId === 1) {
        if (contractOrderKey.trim() !== "") {
          const filterContractOrders = contractOrderDistinctAttributes.filter(x => x.contractOrderKey === contractOrderKey);
          if (filterContractOrders.length === 1) {
            if (filterContractOrders[0].statusId === 2) {
              return true;
            }
          }
        }
      }
      else if (actionId === 2 || actionId === 3) {
        if (reason.trim() !== "") {
          return true;
        }
      }
      else if (actionId === 7) {
        if (crmInteractionId.trim() !== "") {
          return true;
        }
      }

      else {
        return true;
      }
    }
    return false;
  }

  // Get crm cases
  const getCRMCases = async () => {
    setPreOrderCRMCases([]);
    setCRMInteractionId("");
    setLoading(true);

    try {

      const folderId = Number(selectedRows[0].folderId);

      await API.get("/api/crm/proinstall/cases/" + folderId)
        .then(async (response) => {
          setLoading(false);

          const tempCases: PreOrderCRMCase[] = [];
          (response.data.cases as PreOrderCRMCase[]).forEach(item => {
            tempCases.push({ caseNumber: item.caseNumber, installDate: item.installDate, interactionId: item.interactionId, comments: item.comments });
          });
          setPreOrderCRMCases(tempCases);
        })
        .catch((error) => {
          setLoading(false);
          showAlert({
            message: apiReponseError(error, "Failed to get crm cases."),
            severity: "error",
          });
        });
    } catch (e) {
      setLoading(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }

  }

  // Refresh pending promgrams
  const refresh = async () => {
    await getPendingAssingnmentPrograms();
  }

  // Get pending programs
  const filterPendingAssignmentProgramsByStatusId = (id, data) => {

    setStatusId(id);
    if (data === null) {
      data = pendings;
    }

    const rows: any = [];
    let rowId = 0;
    data.filter(pendingStatus => pendingStatus.statusId === id).forEach((row, index) => {
      row.companies.forEach(company => {
        company.programs.forEach(program => {
          rows.push({ ...program, clientCompanyName: company.clientCompanyName, id: rowId });
          rowId++;
        })
      })
    });

    setPendingPrograms(rows);
  }

  const updateContractOrderDistinctAttributes = (data: ContractOrder[] | null): void => {

    if (!data) {
      setContractOrderDistinctAttributes([]);
      return;
    }

    const seen = new Set<string>();

    const distinct = data
      .filter(c => {
        if (seen.has(c.contractOrderKey)) return false;
        seen.add(c.contractOrderKey);
        return true;
      })
      .map<ContractOrderDistinctAttribute>(c => ({
        contractOrderKey: c.contractOrderKey,
        contractOrderNumber: c.contractOrderNumber,
        effectiveDate: c.effectiveDate,
        expirationDate: c.expirationDate,
        taxExempt: c.taxExempt,
        postingStatusId: c.postingStatusId,
        postingStatus: c.postingStatus,
        equipmentVendorId: c.equipmentVendorId,
        equipmentVendor: c.equipmentVendor,
        contractTypeId: c.contractTypeId,
        contractType: c.contractType,
        invoiceStatusId: c.invoiceStatusId,
        invoiceStatus: c.invoiceStatus,
        masterAgreementLevel: c.masterAgreementLevel,
        orderNumber: c.orderNumber,
        override: c.override,
        statusId: c.statusId,
        status: c.status
      }));

    setContractOrderDistinctAttributes(distinct);
  };


  // Get pending assignment programs
  const getPendingAssingnmentPrograms = async () => {
    setPendings([]);
    setPendingPrograms([]);
    setLoading(true);

    try {
      await API.get("/api/preorder/pendings")
        .then(async (response) => {

          setPendings(response.data.pendings);
          setPendingSummaries(response.data.pendingSummaries);

          const systemMatch = (response.data.pendingSummaries as PendingSummaries).matchSummaries.systemMatch;
          const manualMatch = (response.data.pendingSummaries as PendingSummaries).matchSummaries.manualMatch;

          const tempDefaultChartOptionsForBatchMatch = JSON.parse(JSON.stringify(defaultChartOptionsForBatchMatch));
          tempDefaultChartOptionsForBatchMatch.title.text = "COID Match";

          tempDefaultChartOptionsForBatchMatch.series[0].data = [
            ['System (' + systemMatch + ')', systemMatch],
            ['Manual (' + manualMatch + ')', manualMatch],
          ]
          setChartOptionsForBatchMatchSummary(tempDefaultChartOptionsForBatchMatch);

          const tempDefaultChartOptionsForPreOrderType = JSON.parse(JSON.stringify(defaultChartOptionsForBatchMatch));
          tempDefaultChartOptionsForPreOrderType.title.text = "Batch Type";
          const preOrderTypeData: any = [];


          (response.data.pendingSummaries as PendingSummaries).preOrderTypeSummaries.forEach(item => {
            preOrderTypeData.push([item.title + '(' + item.count + ')', item.count]);
          })

          tempDefaultChartOptionsForPreOrderType.series[0].data = preOrderTypeData;
          setChartOptionsForPreOrderTypeSummary(tempDefaultChartOptionsForPreOrderType);

          if (response.data.pendings.length > 0) {
            setTimeout(() => {
              filterPendingAssignmentProgramsByStatusId(response.data.pendings[0].statusId as number, response.data.pendings);
              setLoading(false);
            }, 2000);
          }
          else {
            setLoading(false);
          }


        })
        .catch((error) => {
          setLoading(false);
          showAlert({
            message: apiReponseError(error, "Failed to get pending programs."),
            severity: "error",
          });
        });
    } catch (e) {
      setLoading(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Get all pending program assignment preorder to review
  const getPendingProgramPreorderAssignments = async () => {
    getPendingProgramPreorderAssignmentsByProgramKey(programKey);
  }

  // Get all pending program assignment preorder to review
  const getPendingProgramPreorderAssignmentsByProgramKey = async (programKey) => {

    setProgramKey(programKey);
    setLoading(true);

    setPreorderAssignments([]);
    setFilterAssignments([]);
    setAssignmentFilterAttribute(defaultAssignmentFilterAttribute);
    setSelectedRows([]);

    try {
      await API.get("/api/preorder/pending/programs/" + programKey + "/" + statusId + "/assignments")
        .then(async (response) => {
          const rows: any = [];

          response.data.pendingAssignments.map((pendingAssignment, index) =>
            rows.push({ id: (index + 1), ...pendingAssignment })
          );

          if (rows.length === 0) {
            resetAll();
            redirectRoutePage("preordercorrections");
            return;
          }

          setPreorderAssignments(rows);

          getFilterPendingProgramPreorderAssignments(rows, defaultAssignmentFilterAttribute);

          setContractOrders(response.data.contractOrders);
          updateContractOrderDistinctAttributes(response.data.contractOrders);

          setLoading(false);

        })
        .catch((error) => {
          setLoading(false);
          showAlert({
            message: apiReponseError(error, "Failed to get orders."),
            severity: "error",
          });
        });
    } catch (e) {
      setLoading(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };


  // Filter (additional) pending program assignment preorder to review
  const getFilterPendingProgramPreorderAssignments = (data, filter: AssignmentFilterAttribute) => {
    setFilterAssignments([]);

    let tempFilter: any[] = preorderAssignments;

    if (data !== null) {
      tempFilter = data;
    }

    if (filter.kitName !== "") {
      tempFilter = tempFilter.filter(item => item.kitName === filter.kitName);
    }

    if (filter.chargeableType !== "") {
      tempFilter = tempFilter.filter(item => item.chargeableType === filter.chargeableType);
    }

    if (filter.postingStatus !== "") {
      tempFilter = tempFilter.filter(item => item.postingStatus === filter.postingStatus);
    }

    setFilterAssignments(tempFilter);
  }

  // User click pending program to get assigments
  const handleDataGridClickForPendingPrograms = (params: GridRowParams, event: any) => {
    getPendingProgramPreorderAssignmentsByProgramKey(params.row.programKey);
  };


  // Get distinct values to populate dropdown list for filtering preorders
  const distinctGridDataValues = (columnName: string) => {
    let uniqueValues = [""];
    let columnValue: any = "";
    try {
      preorderAssignments.forEach((assignment) => {
        if (assignment.hasOwnProperty(columnName)) {
          columnValue = assignment[columnName];
          if (uniqueValues.indexOf(columnValue) === -1) {
            uniqueValues.push(columnValue);
          }
        }
      });
    }
    catch (e) { }
    uniqueValues = uniqueValues.filter(x => x !== "");

    return uniqueValues;
  }

  // const clearGridDataFilterInputs = () => {
  //   setAssignmentFilterAttribute(defaultAssignmentFilterAttribute);
  //   getFilterPendingProgramPreorderAssignments(null, defaultAssignmentFilterAttribute);
  // }

  const savePendingAssignments = async () => {
    setLoading(true);

    try {

      setDataChange(true);
      const preOrderKeys: any = [];

      selectedRows.forEach(selectedRow => {
        preOrderKeys.push(selectedRow.preOrderKey);
      });

      // let tempContractOrderKey = "";
      // let folderId = 0;
      // if (contractOrderKey.trim() !== "") {
      //   const filterContractOrders = contractOrderDistinctAttributes.filter(x => x.contractOrderKey  === contractOrderKey);

      //   if (filterContractOrders.length === 1) {
      //     tempContractOrderKey = filterContractOrders[0].contractOrderKey;
      //     folderId = filterContractOrders[0].folderId;
      //   }
      // }

      const payload = {
        actionId: actionId,
        contractOrderKey: contractOrderKey,
        folderId: 0,
        preOrderKeys: preOrderKeys,
        reason: reason,
        crmCaseNumber: "",
        crmInteractionId: "",
        crmInstallDate: null,
        crmComments: ""
      }

      if (crmInteractionId !== "") {
        var filterCases = preOrderCRMCases.filter(x => x.interactionId === crmInteractionId);
        if (filterCases.length === 1) {
          payload.crmCaseNumber = filterCases[0].caseNumber;
          payload.crmInteractionId = filterCases[0].interactionId;
          payload.crmInstallDate = filterCases[0].installDate;
          payload.crmComments = filterCases[0].comments;
        }
      }

      await API.post("/api/preorder/pending/" + statusId + "/assignments", payload)
        .then(async (response) => {

          showAlert({
            message: "Successfully updated all the row(s).",
            severity: "success",
          });

          setLoading(false);

          getPendingProgramPreorderAssignments();

        })
        .catch((error) => {
          setLoading(false);
          showAlert({
            message: apiReponseError(error, "Failed to save."),
            severity: "error",
          });
        });
    } catch (e) {
      setLoading(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  useEffect(() => {
    setWindowTitle("Pre-Order");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);

      if (!canReadPreOrder(userFunctionAccess)) {
        showAlert({ message: "Access Denied", severity: "error" });
        goToRoutePage("Home");
      }

      setReadonly(!canWritePreOrder(userFunctionAccess));

      setShowDashboard(getShowDashboardValue("ShowPreOrderDashboard", true));

      getPendingAssingnmentPrograms();
    };

    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated]);

  return (
    <React.Fragment>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>

          <Confirm
            title={confirmationDialogSetting.title}
            content={confirmationDialogSetting.content}
            yesText={confirmationDialogSetting.yesText}
            noText={confirmationDialogSetting.noText}
            open={confirmationDialogSetting.open}
            callbackOnClose={confirmationDialogSetting.callbackOnClose}
            callbackOnConfirm={confirmationDialogSetting.callbackOnConfirm}
          />

          <Grid container spacing={3}>
            <Grid item xs={9}>
              <Box className={globalStyles.bottomSpacer}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                    Home
                  </Link>
                  <Typography color="textPrimary">Preorder Corrections</Typography>
                </Breadcrumbs>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Hidden xsDown>
                <Box display="flex" flexDirection="row-reverse">
                  {
                    programKey === "" ?
                      <IconButton>
                        <Tooltip arrow title={"Refresh"}>
                          <RefreshIcon color="secondary" onClick={() => refresh()} />
                        </Tooltip>
                      </IconButton>
                      : null
                  }

                  {
                    canReadContractOrder(userFunctionAccess) ?
                      <IconButton>
                        <Tooltip arrow title={"Contract Orders"}>
                          <ContractOrderIcon color="secondary" onClick={() => goToRoutePage('contractorders')} />
                        </Tooltip>
                      </IconButton>
                      : null
                  }

                  <IconButton onClick={() => setShowDashboard(toggleShowDashboard("ShowPreOrderDashboard", showDashboard))}>
                    <Tooltip arrow title={!showDashboard ? "Show Dashboard" : "Hide Dashboard"}>
                      {!showDashboard ? <DashboardOffIcon color="secondary" /> : <DashboardOnIcon color="secondary" />}
                    </Tooltip>
                  </IconButton>
                </Box>
              </Hidden>

              <Hidden smUp>
                <Box display="flex" justifyContent="flex-end" width={1}>
                  <Tooltip arrow title="Menu">
                    <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleAnchorElOptionsMenuClick}>
                      <MoreIcon />
                    </Button>
                  </Tooltip>
                </Box>
              </Hidden>

              <Menu elevation={2} id="grid-menu" anchorEl={anchorElForOptionsMenu} keepMounted open={Boolean(anchorElForOptionsMenu)} onClose={handleAnchorElOptionsMenuClose}>

                <StyledMenuItem
                  onClick={(e) => {
                    setShowDashboard(toggleShowDashboard("ShowPreOrderDashboard", showDashboard));
                    handleAnchorElOptionsMenuClose();
                  }}
                >
                  <ListItemIcon>
                    {!showDashboard ? <DashboardOffIcon fontSize="small" /> : <DashboardOnIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText primary={!showDashboard ? "Show Dashboard" : "Hide Dashboard"} />
                </StyledMenuItem>

                {
                  canReadContractOrder(userFunctionAccess) ?
                    <StyledMenuItem
                      onClick={(e) => {
                        goToRoutePage('contractorders');
                        handleAnchorElOptionsMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <ContractOrderIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Contract Orders" />
                    </StyledMenuItem>
                    : null
                }

                <StyledMenuItem
                  onClick={(e) => {
                    refresh()
                    handleAnchorElOptionsMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <RefreshIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Refresh" />
                </StyledMenuItem>


              </Menu>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} lg={showDashboard ? 9 : 12}>
              <Grid container spacing={3}>
                {
                  pendings.length === 10 ?
                    <Grid item xs={12}>
                      No daa
                    </Grid>
                    :
                    <Grid item xs={12} >
                      <Grid container spacing={3}>

                        {
                          programKey === "" ?
                            <>
                              <Grid item xs={12}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} sm={6} md={6} lg={4} >
                                    <FormControl required className={globalStyles.formControl} fullWidth={true}>
                                      <InputLabel shrink={true}>Correction Status</InputLabel>
                                      <Select
                                        required
                                        native
                                        labelId="statusId-label"
                                        id="statusId"
                                        name="statusId"
                                        value={statusId}
                                        onChange={(e) => {
                                          filterPendingAssignmentProgramsByStatusId(Number(e.currentTarget.value), null);

                                        }}
                                      >
                                        <option value={0}></option>
                                        {
                                          pendings.map((pendingStatus, index) => (
                                            <option value={pendingStatus.statusId} key={index}>{pendingStatus.status}</option>
                                          ))
                                        }
                                      </Select>
                                      <FormHelperText error>{statusId === 0 ? "Correction status is required." : null}</FormHelperText>
                                    </FormControl>
                                  </Grid>

                                  <Grid item xs={12}>
                                    <Box className={pageStyles.programContainer}>
                                      <CustomDataGrid
                                        uniqueGridCode={"PreOrdersPendingProgramsResult"}
                                        columns={columnsForPendingPrograms}
                                        rows={pendingPrograms}
                                        showToolBar={true}
                                        sortModel={[{ field: "clientCompanyName", sort: "asc" }, { field: "programName", sort: "asc" }]}
                                        handleDataGridClick={handleDataGridClickForPendingPrograms} />

                                    </Box>

                                  </Grid>

                                </Grid>
                              </Grid>
                            </>
                            :
                            <>
                              <Grid item xs={12}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                      {
                                        preorderAssignments.length > 0 ?
                                          <>

                                            {
                                              statusId === 20 && contractOrderDistinctAttributes.length === 0 ?
                                                <Grid item xs={12}>
                                                  <Alert severity="warning">
                                                    Sorry, contract order is not available for this program.
                                                    {
                                                      canWriteContractOrder(userFunctionAccess) ?
                                                        <span>&nbsp;Please click on the <span onClick={newContractOrder}><b><u>link</u></b></span> to create a new contract order before selecting any rows.</span>
                                                        : null
                                                    }
                                                  </Alert>
                                                </Grid>
                                                : null
                                            }

                                            <Grid item xs={12} sm={3} md={4} lg={2}   >
                                              {
                                                pendings.filter(item => item.statusId === statusId).map((item, index) => (
                                                  <TextField disabled={true} label="Status" value={item.status} fullWidth={true} />
                                                ))
                                              }
                                            </Grid>

                                            <Grid item xs={12} sm={3} md={4} lg={2}   >
                                              {
                                                pendings.filter(item => item.statusId === statusId).map((item, index) => (
                                                  item.companies.map((company, index) => (
                                                    company.programs.filter(program => program.programKey === programKey).map((program, index) => (
                                                      <TextField disabled={true} label="Client Company" value={company.clientCompanyName} fullWidth={true} />
                                                    ))
                                                  ))
                                                ))
                                              }
                                            </Grid>

                                            <Grid item xs={12} sm={3} md={4} lg={2}   >
                                              {
                                                pendings.filter(item => item.statusId === statusId).map((item, index) => (
                                                  item.companies.map((company, index) => (
                                                    company.programs.filter(program => program.programKey === programKey).map((program, index) => (
                                                      <TextField disabled={true} label="Program" value={program.programName} fullWidth={true} />
                                                    ))
                                                  ))
                                                ))
                                              }
                                            </Grid>
                                            {
                                              !readonly ?
                                                <>
                                                  <Grid item xs={12} sm={3} md={4} lg={2}   >
                                                    <FormControl className={globalStyles.formControl} fullWidth={true}>
                                                      <InputLabel shrink={true}>Posting Status</InputLabel>
                                                      <Select
                                                        native
                                                        value={assignmentFilterAttribute.postingStatus}
                                                        onChange={(e) => {
                                                          var tempFilterAttribute = { ...assignmentFilterAttribute, postingStatus: e.currentTarget.value as string };
                                                          setAssignmentFilterAttribute(tempFilterAttribute)
                                                          getFilterPendingProgramPreorderAssignments(null, tempFilterAttribute)
                                                        }}
                                                      >
                                                        <option value={""}></option>
                                                        {
                                                          distinctGridDataValues("postingStatus").map((item, index) => (
                                                            <option value={item} key={index}>{item}</option>
                                                          ))
                                                        }
                                                      </Select>
                                                    </FormControl>
                                                  </Grid>

                                                  <Grid item xs={12} sm={3} md={4} lg={2}   >
                                                    <FormControl className={globalStyles.formControl} fullWidth={true}>
                                                      <InputLabel shrink={true}>Chargable Type</InputLabel>
                                                      <Select
                                                        native
                                                        value={assignmentFilterAttribute.chargeableType}
                                                        onChange={(e) => {
                                                          var tempFilterAttribute = { ...assignmentFilterAttribute, chargeableType: e.currentTarget.value as string };
                                                          setAssignmentFilterAttribute(tempFilterAttribute)
                                                          getFilterPendingProgramPreorderAssignments(null, tempFilterAttribute)
                                                        }}
                                                      >
                                                        <option value={""}></option>
                                                        {
                                                          distinctGridDataValues("chargeableType").map((item, index) => (
                                                            <option value={item} key={index}>{item}</option>
                                                          ))
                                                        }
                                                      </Select>
                                                    </FormControl>
                                                  </Grid>

                                                  <Grid item xs={12} sm={3} md={4} lg={2}   >
                                                    <FormControl className={globalStyles.formControl} fullWidth={true}>
                                                      <InputLabel shrink={true}>Kit</InputLabel>
                                                      <Select
                                                        native
                                                        value={assignmentFilterAttribute.kitName}
                                                        onChange={(e) => {
                                                          var tempFilterAttribute = { ...assignmentFilterAttribute, kitName: e.currentTarget.value as string };
                                                          setAssignmentFilterAttribute(tempFilterAttribute)
                                                          getFilterPendingProgramPreorderAssignments(null, tempFilterAttribute)
                                                        }}
                                                      >
                                                        <option value={""}></option>
                                                        {
                                                          distinctGridDataValues("kitName").map((item, index) => (
                                                            <option value={item} key={index}>{item}</option>
                                                          ))
                                                        }
                                                      </Select>
                                                    </FormControl>
                                                  </Grid>
                                                </>
                                                : null
                                            }

                                            {/* <Grid item xs={12} sm={3} md={4} lg={2}  >
                                        <Box p={3} display="flex" justifyContent="center" alignContent="center">
                                          <Button id="button-cancel" color="default" className={globalStyles.buttonDefault} onClick={() => clearGridDataFilterInputs()}>
                                            <u>Clear</u>
                                          </Button>
                                        </Box>
                                      </Grid> */}

                                            <Box p={1}></Box>
                                          </>
                                          : null
                                      }
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12}>
                                    <Box className={globalStyles.topic}>
                                      <Grid container spacing={3}>
                                        <Grid item xs={8}>Review Preorder(s)</Grid>
                                        <Grid item xs={4}>
                                          <Box display="flex" justifyContent="flex-end" alignContent="center" textAlign="right"  >
                                            <span style={{ fontSize: '0.7em', paddingTop: 5 }}>Rows: {filterAssignments.length}</span>
                                          </Box>
                                        </Grid>
                                      </Grid>

                                    </Box>
                                  </Grid>

                                  {
                                    statusId === 20 && (isPostingStatusRequired || !isContractOrderExists) ?
                                      <Grid item xs={12}>
                                        <Alert severity="error">
                                          {
                                            isPostingStatusRequired ?
                                              <span>You cannot update multiple posting status of orders at once. Please use a filter to select specific posting status orders for updates.</span>
                                              :
                                              <span>
                                                No contract order exists for the posting status record selected.
                                                {
                                                  canWriteContractOrder(userFunctionAccess) ?
                                                    <span>&nbsp;Please click on the <span onClick={newContractOrder}><b><u>link</u></b></span> to create a new contract order before selecting any rows.</span>
                                                    : null
                                                }
                                              </span>
                                          }

                                        </Alert>
                                      </Grid>
                                      : null
                                  }

                                  <Grid item xs={12}>
                                    <Box className={pageStyles.datagridContainer}>

                                      {
                                        (statusId === 20 && contractOrderDistinctAttributes.length === 0) || readonly ?

                                          <CustomDataGrid
                                            uniqueGridCode={[15, 16].indexOf(statusId) !== -1 ? "PreOrdersAssignmentsResultW1" : "PreOrdersAssignmentsResultW2"}
                                            columns={[15, 16].indexOf(statusId) !== -1 ? crmColumns : columns}
                                            rows={filterAssignments}
                                            showToolBar={true}
                                            sortModel={[{ field: "clientCompanyName", sort: "asc" }, { field: "programName", sort: "asc" }]}
                                            handleDataGridClick={() => { }} />
                                          :
                                          <CustomDataRowSelectionGrid
                                            uniqueGridCode={[15, 16].indexOf(statusId) !== -1 ? "PreOrdersAssignmentsResultR1" : "PreOrdersAssignmentsResultR2"}
                                            columns={[15, 16].indexOf(statusId) !== -1 ? crmColumns : columns}
                                            rows={filterAssignments}
                                            showToolBar={true}
                                            sortModel={[{ field: "clientCompanyName", sort: "asc" }, { field: "programName", sort: "asc" }]}
                                            handleDataGridClick={() => { }}
                                            handleSelectedRows={handleSelectedRowsForPendingAssignments} />
                                      }
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Grid>

                              {
                                selectedRows.length > 0 && !readonly ?
                                  <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                      <Grid item xs={12}>
                                        <Box className={globalStyles.topic}>
                                          Action
                                        </Box>
                                      </Grid>

                                      <Grid item xs={12}>
                                        <Grid container spacing={3}>

                                          <Grid item xs={12} sm={6} md={4} >
                                            <FormControl className={globalStyles.formControl} fullWidth={true} error={actionId <= 0}>
                                              <InputLabel shrink={true}>Actions on selected {selectedRows.length} row(s)</InputLabel>
                                              <Select
                                                required
                                                native
                                                labelId="actionId-label"
                                                id="actionId"
                                                name="actionId"
                                                value={actionId}
                                                onChange={(e) => {
                                                  const currentActionId = Number(e.currentTarget.value);

                                                  setActionId(currentActionId);
                                                  if (currentActionId === 7) {
                                                    getCRMCases();
                                                  }
                                                  else {
                                                    setPreOrderCRMCases([]);
                                                  }
                                                }}
                                              >
                                                <option value={0}></option>
                                                {
                                                  pendings.filter(pendingStatus => pendingStatus.statusId === statusId).map((pendingStatus, index) => (
                                                    pendingStatus.actions.map((action, index) => (
                                                      <option value={action.id} key={index}>{action.name}</option>
                                                    ))
                                                  ))
                                                }
                                              </Select>
                                              <FormHelperText error>{actionId <= 0 ? "Action is required." : null}</FormHelperText>
                                            </FormControl>
                                          </Grid>

                                          {
                                            actionId === 1 ?
                                              <>
                                                <Grid item xs={12} sm={6} md={8}>
                                                  <FormControl className={globalStyles.formControl} fullWidth={true} error={contractOrderKey.trim() === ""}>
                                                    <InputLabel shrink={true}>Contract Order</InputLabel>
                                                    <Select
                                                      disabled={isPostingStatusRequired || !isContractOrderExists}
                                                      required
                                                      native
                                                      labelId="contractOrderKey-label"
                                                      id="contractOrderKey"
                                                      name="contractOrderKey"
                                                      value={contractOrderKey}
                                                      onChange={(e) => {
                                                        if (e.currentTarget.value === "nco") {
                                                          openConfirmationDialog("nco");
                                                          setContractOrderKey("");
                                                        }
                                                        else {
                                                          setContractOrderKey(e.currentTarget.value as string);
                                                        }
                                                      }}
                                                      error={contractOrderKey.trim() === ""}  >
                                                      <option value={""}></option>
                                                      {
                                                        contractOrderDistinctAttributes.filter(contractOrder => contractOrder.postingStatus === selectedPostingStatus).map((contractOrder, index) => (
                                                          <option value={contractOrder.contractOrderKey} key={index}>
                                                            {contractOrder.contractOrderNumber}
                                                          </option>
                                                        ))
                                                      }
                                                      {canWriteContractOrder(userFunctionAccess) ?
                                                        <option value={"nco"} style={{ color: 'red' }}>Create New Contract Order</option>
                                                        : null
                                                      }
                                                    </Select>
                                                    <FormHelperText error>{contractOrderKey.trim() === "" ? "Contract order is required." : null}</FormHelperText>
                                                  </FormControl>
                                                </Grid>

                                                {
                                                  contractOrderDistinctAttributes !== null && contractOrderDistinctAttributes.filter(contractOrder => contractOrder.contractOrderKey === contractOrderKey).map((contractOrder, index) => (
                                                    <Grid item xs={12} key={index}>
                                                      <Grid container spacing={3}>
                                                        <Grid item xs={12} sm={6} md={4}  >
                                                          {
                                                            contractOrder.statusId === 2 ?
                                                              <TextField disabled={true} label="Status" value={contractOrder.status} fullWidth={true} />
                                                              :
                                                              <TextField disabled={true} label="Status" value={contractOrder.status} fullWidth={true} className={pageStyles.statusPause} />
                                                          }

                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={4}  >
                                                          <TextField disabled={true} label="Equipment Vendor" value={contractOrder.equipmentVendor} fullWidth={true} />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={4}  >
                                                          <TextField disabled={true} label="Posting Status" value={contractOrder.postingStatus} fullWidth={true} />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={4}  >
                                                          <TextField disabled={true} label="Contract Type" value={contractOrder.contractType} fullWidth={true} />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={4}  >
                                                          <TextField disabled={true} label="Invoice Status" value={contractOrder.invoiceStatus} fullWidth={true} />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={4}  >
                                                          <TextField disabled={true} label="taxExempt" value={contractOrder.taxExempt ? "Yes" : "No"} fullWidth={true} />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={4}  >
                                                          <TextField disabled={true} label="Effective Date" value={formatDateForInput(contractOrder.effectiveDate)} fullWidth={true} />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={4}  >
                                                          <TextField disabled={true} label="Expiration Date" value={formatDateForInput(contractOrder.expirationDate)} fullWidth={true} />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                          {
                                                            contractOrders.filter(c => c.contractOrderKey === contractOrderKey && c.accountNumber !== "").length > 0 && (
                                                              <Box>
                                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} >
                                                                  <Typography variant="caption" gutterBottom>
                                                                    Linked Account(s) / Folder(s)
                                                                  </Typography>
                                                                  <Typography variant="caption" gutterBottom>
                                                                    &nbsp;Account #&nbsp;
                                                                    {
                                                                      contractOrders.filter(c => c.contractOrderKey === contractOrderKey && c.accountNumber !== "").length
                                                                    }

                                                                  </Typography>
                                                                </Box>
                                                                <TableContainer style={{ maxHeight: 300, overflow: "auto" }}>
                                                                  <Table stickyHeader size="small">
                                                                    <TableHead>
                                                                      <TableRow>
                                                                        <TableCell>Account Number</TableCell>
                                                                        <TableCell>Folder Id</TableCell>
                                                                        <TableCell>Folder Name</TableCell>
                                                                      </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                      {
                                                                        contractOrders
                                                                          .filter(c => c.contractOrderKey === contractOrderKey && c.accountNumber !== "")
                                                                          .map((c, index) => (
                                                                            <TableRow key={index}>
                                                                              <TableCell>
                                                                                {c.accountNumber || (
                                                                                  <span className="errorText">Not Found</span>
                                                                                )}
                                                                              </TableCell>
                                                                              <TableCell>
                                                                                {c.folderId > 0 ? c.folderId : (
                                                                                  <span className="errorText">N/A</span>
                                                                                )}
                                                                              </TableCell>
                                                                              <TableCell>
                                                                                {c.folderId > 0 ? c.folderName : (
                                                                                  <span className="errorText">Not Found</span>
                                                                                )}
                                                                              </TableCell>
                                                                            </TableRow>
                                                                          ))}
                                                                    </TableBody>
                                                                  </Table>
                                                                </TableContainer>
                                                              </Box>
                                                            )}
                                                        </Grid>

                                                      </Grid>
                                                    </Grid>
                                                  ))
                                                }
                                              </>
                                              :
                                              (actionId === 2 || actionId === 3) ?
                                                <Grid item xs={12} md={8}   >
                                                  <TextField
                                                    required
                                                    id="reason"
                                                    label="Reason"
                                                    value={reason}
                                                    fullWidth={true}
                                                    onChange={(e) => {
                                                      setReason(e.currentTarget.value as string);
                                                    }}
                                                    error={reason.trim() === ""}
                                                    helperText={reason.trim() === "" ? "Reason is required." : null} />
                                                </Grid>
                                                :
                                                (actionId === 7) ?
                                                  <>
                                                    <Grid item xs={12} md={8}   >
                                                      {
                                                        selectedRows.length === 1 ?
                                                          <FormControl className={globalStyles.formControl} fullWidth={true} error={crmInteractionId.trim() === ""}>
                                                            <InputLabel shrink={true}>CRM Case Number</InputLabel>
                                                            <Select
                                                              disabled={preOrderCRMCases === null || preOrderCRMCases.length === 0}
                                                              required
                                                              native
                                                              labelId="crmInteractionId-label"
                                                              id="crmInteractionId"
                                                              name="crmInteractionId"
                                                              value={crmInteractionId}
                                                              onChange={(e) => {
                                                                setCRMInteractionId(e.currentTarget.value as string);
                                                              }}
                                                              error={crmInteractionId.trim() === ""}  >
                                                              <option value={""}></option>
                                                              {
                                                                preOrderCRMCases !== null && preOrderCRMCases.map((preOrderCRMCase, index) => (
                                                                  <option value={preOrderCRMCase.interactionId} key={index}>
                                                                    {preOrderCRMCase.caseNumber} - ({preOrderCRMCase.interactionId})
                                                                  </option>
                                                                ))
                                                              }

                                                            </Select>
                                                            <FormHelperText error>{preOrderCRMCases.length === 0 ? "Currently, there are no CRM cases available for assignment." : crmInteractionId.trim() === "" ? "CRM case number is required." : null}</FormHelperText>
                                                          </FormControl>
                                                          :
                                                          <FormControl className={globalStyles.formControl} fullWidth={true} error={crmInteractionId.trim() === ""}>
                                                            <InputLabel shrink={true}>CRM Case Number</InputLabel>
                                                            <Select disabled={true}></Select>
                                                            <FormHelperText error>It is not possible to update the CRM case number for multiple selected rows all at once. Each row must be updated individually.</FormHelperText>
                                                          </FormControl>
                                                      }
                                                    </Grid>
                                                    {
                                                      crmInteractionId !== "" ?
                                                        <Grid item xs={12}>

                                                          {
                                                            preOrderCRMCases !== null && preOrderCRMCases.map((preOrderCRMCase, index) => (
                                                              <Grid container spacing={3} key={index}>
                                                                <Grid item xs={12} sm={6} md={4}>
                                                                  <TextField disabled={true} label="Install Date" value={formatDateTimeForDataGrid(preOrderCRMCase.installDate)} fullWidth={true} />
                                                                </Grid>
                                                                <Grid item xs={12} md={8}>
                                                                  <TextField disabled={true} label="Comments" value={preOrderCRMCase.comments} fullWidth={true} />
                                                                </Grid>
                                                              </Grid>
                                                            ))
                                                          }

                                                        </Grid>
                                                        : null
                                                    }
                                                  </>
                                                  : null
                                          }

                                        </Grid>
                                      </Grid>

                                      {/* <Grid item xs={12}>
                                <Box p={3} display="flex" justifyContent="center" alignContent="center">

                                  {
                                    showUpdateButton() ?
                                      <><Button
                                        className={globalStyles.buttonDefault}
                                        color="secondary"
                                        variant='contained'
                                        onClick={() => { openConfirmationDialog("updateassignments"); }}>
                                        Update
                                      </Button>
                                        &nbsp;&nbsp;
                                      </>
                                      : null
                                  }

                                  <Button id="button-cancel" color="default" className={globalStyles.buttonDefault} onClick={() => resetAll()}>
                                    <u>Cancel</u>
                                  </Button>
                                </Box>
                              </Grid> */}

                                    </Grid>
                                  </Grid>
                                  : null
                              }

                              <Grid item xs={12}>
                                <Box p={3} display="flex" justifyContent="center" alignContent="center">

                                  {
                                    showUpdateButton() ?
                                      <><Button
                                        className={globalStyles.buttonDefault}
                                        color="secondary"
                                        variant='contained'
                                        onClick={() => { openConfirmationDialog("updateassignments"); }}>
                                        Update
                                      </Button>
                                        &nbsp;&nbsp;
                                      </>
                                      : null
                                  }

                                  <Button id="button-cancel" color="default" className={globalStyles.buttonDefault} onClick={() => resetAll()}>
                                    <u>Cancel</u>
                                  </Button>
                                </Box>
                              </Grid>
                            </>
                        }
                      </Grid>
                    </Grid>
                }
              </Grid>
            </Grid>
            {
              showDashboard ?
                <Grid item xs={12} sm={12} lg={3}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <JobRunTicker interval={30} unit={'minutes'} mode={0} />
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Card elevation={0} className={globalStyles.rightCardContainer}>
                        <CardHeader title="" subheader="DASHBOARD" />

                        <CardContent>
                          <Grid container spacing={3}>
                            {
                              pendingSummaries !== null ?
                                <>
                                  {
                                    pendingSummaries?.statusSummaries.map((summary, index) => (
                                      <Grid item key={index} xs={4}  >
                                        <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center"  >
                                          <Box>
                                            <Typography variant="h6" className="count"  >
                                              {summary.count}
                                            </Typography>
                                          </Box>
                                          <Box style={{ width: '100%', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                            <Tooltip title={summary.title}>
                                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                                {summary.title}
                                              </Typography>
                                            </Tooltip>
                                          </Box>
                                        </Box>
                                      </Grid>
                                    ))
                                  }
                                </>
                                : null
                            }
                          </Grid>
                        </CardContent>

                        <CardContent >
                          {
                            pendingSummaries !== null ?
                              <HighchartsReact highcharts={Highcharts} options={chartOptionsForBatchMatchSummary} />

                              : null
                          }
                        </CardContent>

                        <CardContent  >
                          {
                            pendingSummaries !== null ?
                              <HighchartsReact highcharts={Highcharts} options={chartOptionsForPreOrderTypeSummary} />

                              : null
                          }
                        </CardContent>
                      </Card>
                    </Grid>
                    {/* <Grid item xs={12} sm={6} md={12} lg={12}>
                      <Card elevation={0} className={globalStyles.rightCardContainer}>
                        <CardHeader title="" subheader="STATUS/PROGRAM SUMMARY" />
                        <CardContent style={{ maxHeight: 250, overflow: 'auto' }}>
                          <Table>
                            <TableBody>
                              {
                                pendingSummaries !== null ?
                                  <>
                                    {
                                      pendingSummaries.statusPendingSummaries.map((status, index) => (
                                        <>
                                          <TableRow key={index} >
                                            <TableCell>
                                              <b>{status.statusName}</b>
                                            </TableCell>
                                          </TableRow>
                                          {
                                            status.programPendingSummaries.map((summary, index) => (
                                              <TableRow key={index} >
                                                <TableCell>
                                                  {summary.programName}
                                                </TableCell>
                                                <TableCell>
                                                  {summary.count}
                                                </TableCell>
                                              </TableRow>
                                            ))
                                          }
                                        </>
                                      ))
                                    }
                                  </>
                                  : null
                              }
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    </Grid> */}
                  </Grid>
                </Grid>
                : null
            }
          </Grid>
          <Box p={3}></Box>

        </Screen>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    button: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    actionsContainer: {
      marginBottom: theme.spacing(2),
    },
    resetContainer: {
      padding: theme.spacing(3),
    },
    datagridContainer: {
      minHeight: "500px",
      height: "500px",
      width: "100%",
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down("lg")]: {
        height: "calc(100vh - 600px)",
        minHeight: "500px",
      },
      [theme.breakpoints.down("md")]: {
        height: "550px",
        minHeight: "500px",
      },
    },
    countContainer: {
      justifyItems: "center",
      cursor: "default",
    },
    programContainer: {
      minHeight: "500px",
      height: "500px",
      width: "100%",
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down("xl")]: {
        height: "calc(100vh - 270px)",
        minHeight: "500px",
      },
      [theme.breakpoints.down("md")]: {
        height: "550px",
        minHeight: "500px",
      },
    },
    statusPause: {
      "& .MuiInputBase-input.Mui-disabled": {
        color: theme.palette.error.main,
        fontWeight: 'bolder',
        animationName: "$blinker",
        animationDuration: "2s",
        animationTimingFunction: "linear",
        animationIterationCount: "infinite",
      }
    },
    "@keyframes blinker": {
      from: { opacity: 1 },
      to: { opacity: 0 },
    },
  })
);

export default PreOrderList;
